import {
 Box,
 Checkbox,
 CircularProgress,
 FormControlLabel,
 FormGroup,
 IconButton,
 Typography,
} from '@mui/material';
import { useEffect, MutableRefObject } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { UsersDTO } from '@/redux/identity/identityTypes';
import { RootState } from '@/redux/store';
import {
 usersDetachDatabaseStart,
 getItemStart,
 usersDetachDatabaseFinish,
} from '@/redux/users/usersSlice';
import { useConfirmationDialog } from '@/contexts/useConfirmationDialog';
import { CustomGridApi } from '../XTable/types';
import IconMinus from '@assets/icons/minus.svg?react';
import { useAuthContext } from '@/contexts/AuthContext';
type DatabaseListProps = {
 user: UsersDTO;
 apiRef: MutableRefObject<CustomGridApi>;
};

const DatabaseList = ({ user, apiRef }: DatabaseListProps) => {
 const dispatch = useDispatch();
 const { t } = useTranslation();
 const { loading, success } = useSelector((state: RootState) => state.users);
 const { openDialog } = useConfirmationDialog();
 const { loginUser } = useAuthContext();

 const handleToggleDatabase = (databaseId: number) => {
  openDialog({
   title: t('user.sidebar.databases.detachTitle'),
   message: t('user.sidebar.databases.detachMessage'),
   buttonText: t('user.sidebar.databases.detachConfirm'),
   onConfirm: () => {
    if (!user.Id) {
     return;
    }
    dispatch(
     usersDetachDatabaseStart([
      {
       ConnectionKeyId: databaseId,
       UserId: user.Id,
      },
     ]),
    );
   },
  });
 };

 useEffect(() => {
  if (!success?.usersDetachDatabase) {
   return;
  }
  apiRef.current.fetchData?.();
  if (!user.Id) {
   return;
  }
  dispatch(
   getItemStart({
    table: 'users',
    id: user.Id,
   }),
  );
  dispatch(usersDetachDatabaseFinish());
 }, [apiRef, dispatch, success?.usersDetachDatabase, user.Id]);

 const isLoading = !!loading?.usersDetachDatabase;
 const databases = user.Databases || [];

 return (
  <Box sx={{}}>
   <Typography
    variant="subtitle2"
    sx={{ fontSize: 14, fontWeight: 500, pt: 0, pb: 1 }}
   >
    {t('user.sidebar.databases.title')}
   </Typography>
   {isLoading && (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
     <CircularProgress size={24} />
    </Box>
   )}
   {!isLoading && (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
     {databases.map((database) => {
      const id = database.Id ?? 0;
      const isCurrentDatabase =
       loginUser?.currentlyActiveDb === (database.Id?.toString() || '');
      return (
       <Box
        key={database.Id}
        sx={{
         py: '4px',
         pl: 1,
         pr: '4px',
         border: '1px solid',
         borderColor: (theme) => theme.palette.kemek.lightestGray,
         borderRadius: '4px',
         display: 'flex',
         alignItems: 'center',
         justifyContent: 'space-between',
         minHeight: 36,
        }}
       >
        <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
         {database.Client ||
          `${t('user.sidebar.databases.databaseFallback')} ${id}`}
        </Typography>
        {isCurrentDatabase && (
         <IconButton
          onClick={() => handleToggleDatabase(id)}
          disabled={isLoading}
         >
          <IconMinus />
         </IconButton>
        )}
       </Box>
      );
     })}
     {databases.length === 0 && (
      <Typography
       sx={{
        fontSize: 12,
        color: (theme) => theme.palette.kemek.darkGray,
       }}
      >
       {t('user.sidebar.databases.empty')}
      </Typography>
     )}
    </Box>
   )}
  </Box>
 );
};

export default DatabaseList;
